import React, { useMemo, useState } from 'react';
import { Table, Badge, Spinner, Button, InputGroup, Form } from 'react-bootstrap';
import { Search, Eye, Phone } from 'lucide-react';

const formatCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(n || 0);

/**
 * Tab "Khách hàng loyal" — flatten từ products table.
 * Gom (phone × product) có purchase_count >= loyal_threshold, sort desc.
 */
const TopLoyalCustomersTable = ({ productsData, loyalThreshold, loading, onShowCustomerHistory, onLoadMoreForProduct }) => {
  const [search, setSearch] = useState('');

  // Hiển thị theo product: mỗi product có buyers_Nx ≥ threshold.
  // Chỉ list các product có khách loyal, click vào sẽ mở panel xem chi tiết khách.
  const loyalProducts = useMemo(() => {
    if (!productsData?.rows) return [];
    return productsData.rows
      .map((r) => {
        // Số khách loyal trong product này
        let loyalCount = 0;
        if (loyalThreshold <= 1) loyalCount = r.total_customers;
        else if (loyalThreshold === 2) loyalCount = r.buyers_2x + r.buyers_3x + r.buyers_4x + r.buyers_5x_plus;
        else if (loyalThreshold === 3) loyalCount = r.buyers_3x + r.buyers_4x + r.buyers_5x_plus;
        else if (loyalThreshold === 4) loyalCount = r.buyers_4x + r.buyers_5x_plus;
        else loyalCount = r.buyers_5x_plus;
        return { ...r, loyal_count: loyalCount };
      })
      .filter((r) => r.loyal_count > 0)
      .filter((r) => {
        const needle = search.trim().toLowerCase();
        if (!needle) return true;
        return r.menu_item_name.toLowerCase().includes(needle);
      })
      .sort((a, b) => b.loyal_count - a.loyal_count);
  }, [productsData, loyalThreshold, search]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <InputGroup style={{ maxWidth: 320 }}>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Tìm món…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <div className="text-muted small">
          Loyal = mua ≥ <Badge bg="warning">{loyalThreshold} lần</Badge> — chỉnh ngưỡng ở bộ lọc phía trên.
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" /> <span className="ms-2">Đang tải…</span>
        </div>
      ) : loyalProducts.length === 0 ? (
        <div className="text-center py-5 text-muted">
          Chưa có món nào có khách mua ≥ {loyalThreshold} lần trong khoảng thời gian.
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Món</th>
                <th className="text-center">Khách loyal</th>
                <th className="text-center">Tổng khách</th>
                <th className="text-end">Tổng đơn</th>
                <th className="text-end">Doanh thu</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loyalProducts.map((r) => (
                <tr key={r.menu_item_id}>
                  <td>
                    <div className="fw-semibold">{r.menu_item_name}</div>
                    {r.category_name && <div className="small text-muted">{r.category_name}</div>}
                  </td>
                  <td className="text-center">
                    <Badge bg="success" className="p-2">
                      <Phone size={12} className="me-1" />
                      {r.loyal_count}
                    </Badge>
                  </td>
                  <td className="text-center text-muted">{r.total_customers}</td>
                  <td className="text-end">{r.total_orders.toLocaleString('vi-VN')}</td>
                  <td className="text-end">{formatCurrency(r.total_revenue)} ₫</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => onLoadMoreForProduct(r)}>
                      <Eye size={14} /> Xem danh sách khách
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default TopLoyalCustomersTable;
